import { useState, useEffect, useCallback, useRef } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  Card, Button, Modal, Input, Select, Textarea, StatusBadge, EmptyState, SearchBar,
  Pagination, FilterBar, PageHeader, FilterSelect, ListSkeleton, ExpiryCell, ExpiryBadge,
  AlertBanner, IconButton, StickyActions, Divider, cx,
  ListPage, ListSurface, stickyHeadCls, actionColCls, actionHeadCls,
  listTableCls, listCardsCls, thCls, tdCls, actionW,
} from "../components/UI";
import api from "../utils/api";
import toast from "react-hot-toast";
import { Plus, Pencil, Trash2, Telescope, MapPin, CalendarClock } from "lucide-react";

const LIMIT = 15;
const INIT = { name: "", serialNumber: "", type: "", brand: "", model: "", calibrationDate: "", nextCalibrationDate: "", status: "available", notes: "" };

const TYPES = ["total-station", "gps", "level", "theodolite", "drone", "scanner", "other"];
const STATUSES = ["available", "assigned", "maintenance", "inactive"];
const typeIcon = { "total-station": "🔭", gps: "📡", level: "⚖️", theodolite: "🔭", drone: "🚁", scanner: "📟", other: "🔧" };

function InstrumentCard({ inst, onEdit, onDelete }) {
  return (
    <Card className="p-4" hover>
      {/* actions lead the card, mirroring the table's first column */}
      <div className="flex items-center gap-1.5 pb-3 mb-3 border-b border-ink-100">
        <Button size="xs" variant="outline" icon={<Pencil size={13} />} onClick={() => onEdit(inst)}>Edit</Button>
        <Button size="xs" variant="dangerSoft" icon={<Trash2 size={13} />} onClick={() => onDelete(inst._id)}>Delete</Button>
      </div>

      <div className="flex items-start gap-3">
        <span className="w-11 h-11 rounded-xl bg-violet-50 border border-violet-200 grid place-items-center text-lg shrink-0">
          {typeIcon[inst.type] || "🔭"}
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-ink-900 truncate">{inst.name}</div>
          <div className="text-xs text-ink-400 truncate mt-0.5 capitalize">
            {[inst.type, inst.brand, inst.model].filter(Boolean).join(" · ") || "—"}
          </div>
        </div>
        <StatusBadge status={inst.status || "available"} />
      </div>

      <div className="flex items-center gap-2 flex-wrap mt-3">
        {inst.serialNumber && (
          <span className="font-mono text-[11px] bg-ink-100 border border-ink-200 px-2 py-1 rounded-md text-ink-700">{inst.serialNumber}</span>
        )}
        {inst.assignedSite?.name && (
          <span className="inline-flex items-center gap-1 text-xs text-ink-500">
            <MapPin size={12} className="text-ink-400" />{inst.assignedSite.name}
          </span>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-ink-100">
        {inst.nextCalibrationDate
          ? <ExpiryBadge date={inst.nextCalibrationDate} label="Next calibration" />
          : <span className="text-xs text-ink-300">Calibration not set</span>}
      </div>
    </Card>
  );
}

export default function AdminInstruments() {
  const [instruments, setInstruments] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fType, setFType] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INIT);
  const [saving, setSaving] = useState(false);
  const debRef = useRef(null);

  const fetchInstruments = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, limit: LIMIT });
      if (search) p.set("search", search);
      if (fStatus) p.set("status", fStatus);
      if (fType) p.set("type", fType);
      const { data } = await api.get(`/instruments?${p}`);
      setInstruments(data.data.instruments || []); setTotal(data.data.total); setPages(data.data.pages);
    } catch { toast.error("Failed to load instruments"); }
    finally { setLoading(false); }
  }, [page, search, fStatus, fType]);

  useEffect(() => { fetchInstruments(); }, [fetchInstruments]);

  const handleSearch = (v) => {
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => { setSearch(v); setPage(1); }, 300);
    setSearch(v);
  };

  const openCreate = () => { setForm(INIT); setSelected(null); setModal("create"); };
  const openEdit = (i) => {
    setSelected(i);
    setForm({
      name: i.name || "", serialNumber: i.serialNumber || "", type: i.type || "", brand: i.brand || "", model: i.model || "",
      calibrationDate: i.calibrationDate ? i.calibrationDate.split("T")[0] : "",
      nextCalibrationDate: i.nextCalibrationDate ? i.nextCalibrationDate.split("T")[0] : "",
      status: i.status || "available", notes: i.notes || "",
    });
    setModal("edit");
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (modal === "create") { await api.post("/instruments", form); toast.success("Instrument added!"); }
      else { await api.put(`/instruments/${selected._id}`, form); toast.success("Updated!"); }
      setModal(null); fetchInstruments();
    } catch (err) { toast.error(err.response?.data?.message || "Failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this instrument?")) return;
    try { await api.delete(`/instruments/${id}`); toast.success("Deleted"); fetchInstruments(); }
    catch { toast.error("Failed"); }
  };

  const f = k => ({ value: form[k] || "", onChange: e => setForm({ ...form, [k]: e.target.value }) });

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const calibrationDue = instruments.filter(i =>
    i.nextCalibrationDate && (new Date(i.nextCalibrationDate) - today) / 86400000 <= 30);

  const anyFilter = search || fStatus || fType;
  const clearAll = () => { setSearch(""); setFStatus(""); setFType(""); setPage(1); };

  return (
    <AdminLayout fullHeight>
      <ListPage>
        <PageHeader
          title="Instruments"
          subtitle={`${total} instrument${total !== 1 ? "s" : ""} in inventory`}
          action={<Button onClick={openCreate} icon={<Plus size={16} />} className="w-full sm:w-auto">Add Instrument</Button>}
        />

        {calibrationDue.length > 0 && (
          <div className="shrink-0">
            <AlertBanner type="warning"
              title={`${calibrationDue.length} instrument${calibrationDue.length > 1 ? "s" : ""} due for calibration`}
              message={calibrationDue.map(i => i.name).join(", ")} />
          </div>
        )}

        <FilterBar className="shrink-0">
          <SearchBar value={search} onChange={handleSearch} placeholder="Search name, serial, brand…" className="flex-1 min-w-[170px]" />
          <FilterSelect value={fStatus} onChange={v => { setFStatus(v); setPage(1); }} ariaLabel="Filter by status" className="min-w-[130px]">
            <option value="">All Status</option>
            {STATUSES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
          </FilterSelect>
          <FilterSelect value={fType} onChange={v => { setFType(v); setPage(1); }} ariaLabel="Filter by type" className="min-w-[140px]">
            <option value="">All Types</option>
            {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </FilterSelect>
          {anyFilter && <button onClick={clearAll} className="text-xs font-semibold text-brand-600 hover:text-brand-700 px-1">Clear</button>}
        </FilterBar>

        {loading ? (
          <div className="flex-1 min-h-0 overflow-hidden"><ListSkeleton rows={7} cols={7} /></div>
        ) : instruments.length === 0 ? (
          <Card className="flex-1 min-h-0 grid place-items-center">
            <EmptyState icon={<Telescope size={22} />} title="No instruments found"
              description={anyFilter ? "Try adjusting your search or filters." : "Add your first instrument to the inventory."}
              action={anyFilter
                ? <Button variant="secondary" onClick={clearAll}>Clear filters</Button>
                : <Button onClick={openCreate} icon={<Plus size={16} />}>Add Instrument</Button>} />
          </Card>
        ) : (
          <ListSurface footer={<Pagination page={page} pages={pages} total={total} limit={LIMIT} onPage={setPage} />}>
            {/* ── Desktop table ── */}
            <table className={listTableCls}>
              <thead className={stickyHeadCls}>
                <tr>
                  <th className={cx(thCls, actionHeadCls, actionW(2))}>Actions</th>
                  <th className={thCls}>Instrument</th>
                  <th className={cx(thCls, "w-[140px]")}>Serial No.</th>
                  <th className={cx(thCls, "w-[130px]")}>Type / Brand</th>
                  <th className={thCls}>Site</th>
                  <th className={cx(thCls, "w-[150px]")}>Calibration</th>
                  <th className={cx(thCls, "w-[120px]")}>Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {instruments.map(inst => (
                  <tr key={inst._id} className="group hover:bg-ink-50 transition-colors">
                    <td className={cx(tdCls, actionColCls, "px-3")}>
                      <div className="flex gap-0.5">
                        <IconButton size="sm" variant="outline" icon={<Pencil size={15} />} onClick={() => openEdit(inst)} title="Edit" />
                        <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => handleDelete(inst._id)} title="Delete" />
                      </div>
                    </td>
                    <td className={tdCls}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="w-9 h-9 rounded-xl bg-violet-50 border border-violet-200 grid place-items-center text-base shrink-0">
                          {typeIcon[inst.type] || "🔭"}
                        </span>
                        <div className="min-w-0">
                          <div className="font-semibold text-ink-800 truncate">{inst.name}</div>
                          {inst.model && <div className="text-xs text-ink-400 truncate">{inst.model}</div>}
                        </div>
                      </div>
                    </td>
                    <td className={tdCls}>
                      {inst.serialNumber
                        ? <span className="block font-mono text-[11px] bg-ink-100 border border-ink-200 px-2 py-1 rounded-md text-ink-700 truncate">{inst.serialNumber}</span>
                        : <span className="text-ink-300">—</span>}
                    </td>
                    <td className={cx(tdCls, "text-xs")}>
                      <div className="text-ink-700 capitalize truncate">{inst.type || "—"}</div>
                      {inst.brand && <div className="text-ink-400 truncate">{inst.brand}</div>}
                    </td>
                    <td className={cx(tdCls, "text-xs text-ink-500")}>
                      <div className="truncate">{inst.assignedSite?.name || <span className="text-ink-300">—</span>}</div>
                    </td>
                    <td className={tdCls}>
                      {inst.nextCalibrationDate
                        ? <ExpiryCell date={inst.nextCalibrationDate} label="Due" />
                        : <span className="text-ink-300 text-xs">Not set</span>}
                    </td>
                    <td className={tdCls}><StatusBadge status={inst.status || "available"} /></td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* ── Mobile / tablet cards ── */}
            <div className={listCardsCls}>
              {instruments.map(inst => <InstrumentCard key={inst._id} inst={inst} onEdit={openEdit} onDelete={handleDelete} />)}
            </div>
          </ListSurface>
        )}
      </ListPage>

      <Modal
        open={modal === "create" || modal === "edit"}
        onClose={() => setModal(null)}
        title={modal === "create" ? "Add Instrument" : "Edit Instrument"}
        subtitle={modal === "edit" ? selected?.name : "New inventory item"}
        icon={<Telescope size={18} />}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Instrument Name" required {...f("name")} placeholder="e.g. Total Station #1" />
            <Input label="Serial Number" {...f("serialNumber")} placeholder="SN-XXXX-0001" className="font-mono" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Select label="Type" {...f("type")} wrapClass="col-span-2 sm:col-span-1">
              <option value="">Select type</option>
              {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </Select>
            <Input label="Brand / Make" {...f("brand")} placeholder="Leica" />
            <Input label="Model" {...f("model")} placeholder="TS16" />
          </div>

          <Divider label="Calibration" className="pt-1" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Last Calibration" type="date" {...f("calibrationDate")} icon={<CalendarClock size={15} />} />
            <div className="space-y-2">
              <Input label="Next Calibration Due" type="date" {...f("nextCalibrationDate")} icon={<CalendarClock size={15} />} />
              {form.nextCalibrationDate && <ExpiryBadge date={form.nextCalibrationDate} />}
            </div>
          </div>

          {modal === "edit" && (
            <Select label="Status" {...f("status")}>
              {STATUSES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
            </Select>
          )}
          <Textarea label="Notes" {...f("notes")} placeholder="Accessories, condition, service history…" />

          <StickyActions>
            <Button type="button" variant="secondary" onClick={() => setModal(null)} block className="sm:w-auto">Cancel</Button>
            <Button type="submit" loading={saving} block className="sm:w-auto">
              {modal === "create" ? "Add Instrument" : "Save Changes"}
            </Button>
          </StickyActions>
        </form>
      </Modal>
    </AdminLayout>
  );
}
